<script setup>
import { ref, reactive, computed, onMounted, onActivated } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import { useBase, hora, MONO } from './base';

import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import SandboxAPI from 'dashboard/api/fandata/sandbox';

// T-186 — o comportamento do Bira dentro da mesma porta da base.
//
// Tom, diretrizes e transbordo sempre viajaram DENTRO da versão, junto com as respostas: uma
// publicação leva as duas coisas. O que não existia era uma tela que tratasse os dois do mesmo
// jeito. Mudar a voz do Bira era o único caminho que ia direto ao ar, sem rascunho e sem
// publicar, o que contradizia a primeira regra do T-186: escrever nunca é publicar.
//
// Daí esta tela ser a irmã de "Editar base": mesmo rascunho, mesma barra âmbar, mesmo
// salvamento automático, e a publicação acontece no mesmo lugar, com o diff na frente.
const { t } = useI18n();
const { irPara } = useBase();

const ESPERA_AUTOSAVE = 900;
// T-186: a voz do Bira publica em separado da base, com rascunho próprio. Todas as chamadas
// desta tela viajam com o escopo, senão ela abriria um rascunho e publicaria o outro.
const ESCOPO = 'comportamento';
const CAMPOS = ['tom', 'diretrizes', 'handoff'];

// O que o servidor achou de divergente entre o texto escrito e o que o sistema de fato faz.
// Aparece depois de salvar, enquanto quem escreveu ainda está com o assunto na cabeça: numa
// segunda tela, ou num relatório, o achado chega tarde demais para virar correção.
const conferencia = ref([]);

const carregando = ref(true);
const noAr = reactive({ tom: '', diretrizes: '', handoff: '' });
const rascunho = ref(null);
const publicacao = ref({});
const rotulo = ref('');
const editandoRotulo = ref(false);
const texto = reactive({ tom: '', diretrizes: '', handoff: '' });
const salvando = ref(false);
const ultimoSalvamento = ref(null);
const janela = reactive({ inicio: '', fim: '', fuso: '' });
let cronometro = null;
let abertura = null;

const aberto = computed(() => Boolean(rascunho.value));

// O que muda no comportamento se publicar agora. Vem do servidor (a mesma conta que a tela de
// publicar usa), para as duas telas nunca discordarem sobre o que está pendente.
const mudancas = computed(() =>
  (publicacao.value.mudancas || []).filter(m => m.campo !== 'base')
);

// Esta tela é o editor. A LISTA de versões da voz mora na tela Bira, que é a porta dela: duas
// listas iguais em telas diferentes seriam duas verdades para manter alinhadas.
const carregar = async () => {
  carregando.value = true;
  try {
    const { data } = await SandboxAPI.show(ESCOPO);
    CAMPOS.forEach(campo => {
      noAr[campo] = data.no_ar?.[campo] || '';
      texto[campo] = data.rascunho?.[campo] ?? data.no_ar?.[campo] ?? '';
    });
    rascunho.value = data.rascunho || null;
    rotulo.value = data.rascunho?.rotulo || '';
    publicacao.value = data.publicacao || {};
    Object.assign(janela, data.publicacao?.janela || {});
  } catch (error) {
    useAlert(t('BASE_COMPORTAMENTO.ERRO'));
  } finally {
    carregando.value = false;
  }
};

// Igual à tela de editar: o rascunho nasce na PRIMEIRA escrita, não ao entrar. Visitar a tela
// não pode criar uma versão da base sem ninguém ter mexido em nada.
const garantirRascunho = async () => {
  if (aberto.value) return;

  abertura = abertura || SandboxAPI.abrir(ESCOPO);
  const { data } = await abertura;
  abertura = null;
  rascunho.value = data.rascunho || null;
};

const escrever = () =>
  SandboxAPI.salvar({
    escopo: ESCOPO,
    tom: texto.tom,
    diretrizes: texto.diretrizes,
    handoff: texto.handoff,
    rotulo: rotulo.value,
  });

const salvar = async () => {
  clearTimeout(cronometro);
  salvando.value = true;
  try {
    await garantirRascunho();
    let resposta;
    try {
      resposta = await escrever();
    } catch (erro) {
      // O rascunho pode ter sumido por fora: publicado noutra aba, descartado pela colega. O
      // servidor recusa com 422, e a saída certa é abrir outro e escrever nele, não mandar a
      // pessoa recarregar a página e perder o que digitou.
      if (erro?.response?.status !== 422) throw erro;
      rascunho.value = null;
      await garantirRascunho();
      resposta = await escrever();
    }
    const { data } = resposta;
    rascunho.value = data.rascunho || null;
    publicacao.value = data.publicacao || {};
    conferencia.value = data.conferencia || [];
    ultimoSalvamento.value = new Date();
  } catch (error) {
    useAlert(t('BASE_COMPORTAMENTO.ERRO_SALVAR'));
  } finally {
    salvando.value = false;
  }
};

const agendarSalvamento = () => {
  clearTimeout(cronometro);
  cronometro = setTimeout(salvar, ESPERA_AUTOSAVE);
};

const salvarRotulo = () => {
  editandoRotulo.value = false;
  return salvar();
};

const salvarJanela = async () => {
  try {
    const { data } = await SandboxAPI.janela(janela.inicio, janela.fim);
    publicacao.value = data.publicacao || {};
    useAlert(t('BASE_COMPORTAMENTO.JANELA.SALVA'));
  } catch (error) {
    useAlert(t('BASE_COMPORTAMENTO.ERRO_SALVAR'));
  }
};

// As telas de Configurações vivem dentro de um <keep-alive>: sair e voltar NÃO remonta o
// componente, então `onMounted` não roda de novo e a tela volta com o estado de antes. Depois de
// publicar, isso significa uma tela achando que ainda existe um rascunho que já subiu, e todo
// salvamento batendo em 422. Recarregar ao reativar é o que mantém a tela e o servidor falando
// da mesma coisa.
let jaMontou = false;

onActivated(() => {
  if (!jaMontou) {
    jaMontou = true;
    return;
  }
  carregar();
});

onMounted(carregar);
</script>

<template>
  <SettingsLayout
    :is-loading="carregando"
    :loading-message="$t('BASE_COMPORTAMENTO.HEADER')"
  >
    <template #body>
      <div class="flex flex-col gap-3.5 w-full min-w-0 max-w-[1100px]">
        <div class="flex items-baseline gap-2.5">
          <button
            type="button"
            class="text-[15px] text-n-slate-11 hover:text-n-slate-12"
            @click="irPara('capitao_calibracao')"
          >
            {{ $t('BASE_COMPORTAMENTO.VOLTAR') }}
          </button>
          <h1 class="text-lg font-semibold text-n-slate-12">
            {{ $t('BASE_COMPORTAMENTO.HEADER') }}
          </h1>
        </div>

        <!-- A mesma barra da tela de editar: o rascunho é o mesmo, e as ações também. -->
        <div
          class="sticky top-0 z-30 rounded-md border border-solid border-n-amber-6 bg-n-amber-2 px-[18px] py-3.5 flex flex-wrap items-start gap-x-4 gap-y-2"
        >
          <div class="flex-1 min-w-[300px]">
            <div class="flex items-center gap-2.5 flex-wrap">
              <span
                v-if="aberto"
                :class="MONO"
                class="bg-n-amber-3 text-n-amber-11"
              >
                {{ $t('BASE_COMPORTAMENTO.ETIQUETA') }}
              </span>
              <input
                v-if="editandoRotulo && aberto"
                v-model="rotulo"
                type="text"
                class="reset-base text-sm font-semibold text-n-slate-12 bg-transparent border-b border-solid border-dashed border-n-amber-6 focus:outline-none"
                @blur="salvarRotulo"
                @keyup.enter="salvarRotulo"
              />
              <button
                v-else-if="aberto"
                type="button"
                class="text-sm font-semibold text-n-slate-12 border-b border-dashed border-n-amber-6"
                @click="editandoRotulo = true"
              >
                {{ rotulo || $t('BASE_COMPORTAMENTO.SEM_ROTULO') }}
              </button>
              <span v-else class="text-sm font-medium text-n-slate-11">
                {{ $t('BASE_COMPORTAMENTO.SEM_RASCUNHO') }}
              </span>
            </div>
            <p class="mt-1 text-xs text-n-amber-11/70">
              <template v-if="mudancas.length">
                {{
                  $t('BASE_COMPORTAMENTO.META', {
                    campos: mudancas.map(m => m.rotulo).join(', '),
                  })
                }}
                ·
              </template>
              {{ $t('BASE_COMPORTAMENTO.VIRA_VERSAO') }}
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <span
              v-if="ultimoSalvamento"
              class="font-mono text-[10.5px] tracking-[.06em] text-n-amber-11/70"
            >
              {{
                $t('BASE_COMPORTAMENTO.SALVO_AS', {
                  hora: hora(ultimoSalvamento),
                })
              }}
            </span>
            <Button
              :label="$t('BASE_COMPORTAMENTO.SALVAR')"
              variant="faded"
              color="amber"
              size="sm"
              :is-loading="salvando"
              :disabled="!aberto"
              @click="salvar"
            />
            <Button
              :label="$t('BASE_COMPORTAMENTO.RESPOSTAS')"
              variant="faded"
              color="slate"
              size="sm"
              @click="irPara('capitao_base_editar')"
            />
            <Button
              :label="$t('BASE_COMPORTAMENTO.PUBLICAR')"
              color="amber"
              size="sm"
              :disabled="mudancas.length === 0"
              @click="irPara('capitao_bira_voz_publicar')"
            />
          </div>
        </div>

        <!-- Um bloco por campo, com o que está no ar à esquerda e a cópia editável à direita,
             que é a mesma leitura da tela de editar resposta. -->
        <section
          v-for="campo in CAMPOS"
          :key="campo"
          class="rounded-md border border-solid border-n-weak bg-n-solid-1 overflow-hidden"
        >
          <div class="px-[18px] py-3 border-b border-solid border-n-weak">
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ $t(`BASE_COMPORTAMENTO.CAMPO.${campo.toUpperCase()}.TITULO`) }}
            </h2>
            <p class="mt-0.5 text-[12.5px] text-n-slate-11">
              {{ $t(`BASE_COMPORTAMENTO.CAMPO.${campo.toUpperCase()}.AJUDA`) }}
            </p>
          </div>
          <div class="flex items-stretch">
            <div
              class="w-[340px] flex-none px-5 py-4 border-r border-solid border-n-weak bg-n-alpha-1 flex flex-col gap-2"
            >
              <span
                class="font-mono text-[10px] uppercase tracking-[.06em] text-n-slate-10 whitespace-nowrap"
              >
                {{ $t('BASE_COMPORTAMENTO.NO_AR') }}
              </span>
              <p
                v-if="noAr[campo]"
                class="rounded-[5px] border border-solid border-n-strong bg-n-solid-2 px-3 py-3 text-[13px] leading-relaxed text-n-slate-11 whitespace-pre-line break-words max-h-[260px] overflow-y-auto"
              >
                {{ noAr[campo] }}
              </p>
              <p v-else class="text-[13px] text-n-slate-11">
                {{ $t('BASE_COMPORTAMENTO.PADRAO') }}
              </p>
            </div>
            <div class="flex-1 min-w-0 px-5 py-4 flex flex-col gap-2">
              <span
                class="font-mono text-[10px] uppercase tracking-[.06em] text-n-amber-11 whitespace-nowrap"
              >
                {{ $t('BASE_COMPORTAMENTO.COPIA') }}
              </span>
              <textarea
                v-model="texto[campo]"
                class="m-0 border-solid w-full min-w-0 min-h-[150px] resize-y leading-relaxed rounded-[5px] border border-n-amber-6 bg-n-solid-2 px-2.5 py-2 text-[13px] text-n-slate-12"
                @input="agendarSalvamento"
              />
            </div>
          </div>
        </section>

        <!-- O que o sistema achou de divergente no texto. Fica LOGO ABAIXO do campo, e não numa
             tela de relatório: o achado só vira correção enquanto quem escreveu ainda está com o
             assunto na cabeça. Avisa, nunca bloqueia: quem escreve conhece a operação melhor que
             esta lista de padrões. -->
        <section
          v-if="conferencia.length"
          class="rounded-md border border-n-amber-6 bg-n-amber-2 px-[18px] py-4 flex flex-col gap-2"
        >
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{
              $t('BASE_COMPORTAMENTO.CONFERENCIA.TITULO', {
                n: conferencia.length,
              })
            }}
          </h2>
          <p class="text-[12.5px] text-n-amber-11/70">
            {{ $t('BASE_COMPORTAMENTO.CONFERENCIA.CORPO') }}
          </p>
          <ul class="flex flex-col gap-1.5 m-0 pl-0 list-none">
            <li
              v-for="(achado, i) in conferencia"
              :key="i"
              class="flex flex-wrap items-baseline gap-2 text-[13px] text-n-slate-12"
            >
              <span
                class="font-mono text-[10px] uppercase tracking-[.06em] px-1.5 py-0.5 rounded-sm bg-n-alpha-2 text-n-slate-11 whitespace-nowrap"
              >
                {{
                  $t(
                    `BASE_COMPORTAMENTO.CONFERENCIA.TIPO.${achado.tipo.toUpperCase()}`
                  )
                }}
              </span>
              <span class="flex-1 min-w-[240px]">{{ achado.mensagem }}</span>
            </li>
          </ul>
        </section>

        <!-- A janela é preferência de operação, não conteúdo: vale na hora e não entra na versão. -->
        <section
          class="rounded-md border border-solid border-n-weak bg-n-solid-1 px-[18px] py-4 flex flex-wrap items-end gap-3"
        >
          <div class="flex-1 min-w-[280px]">
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ $t('BASE_COMPORTAMENTO.JANELA.TITULO') }}
            </h2>
            <p class="mt-0.5 text-[12.5px] text-n-slate-11">
              {{ $t('BASE_COMPORTAMENTO.JANELA.AJUDA', { fuso: janela.fuso }) }}
            </p>
          </div>
          <label class="text-[12.5px] text-n-slate-11 flex flex-col gap-1">
            {{ $t('BASE_COMPORTAMENTO.JANELA.INICIO') }}
            <input
              v-model="janela.inicio"
              type="time"
              class="reset-base w-[110px] h-[34px] rounded-[5px] border border-solid border-n-strong bg-n-solid-2 px-2.5 text-[13px] text-n-slate-12"
            />
          </label>
          <label class="text-[12.5px] text-n-slate-11 flex flex-col gap-1">
            {{ $t('BASE_COMPORTAMENTO.JANELA.FIM') }}
            <input
              v-model="janela.fim"
              type="time"
              class="reset-base w-[110px] h-[34px] rounded-[5px] border border-solid border-n-strong bg-n-solid-2 px-2.5 text-[13px] text-n-slate-12"
            />
          </label>
          <Button
            :label="$t('BASE_COMPORTAMENTO.JANELA.SALVAR')"
            variant="faded"
            color="slate"
            size="sm"
            @click="salvarJanela"
          />
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
